export interface ItemField {
  key: string;
  label: string;
  type?: "text" | "textarea";
  hint?: string;
}

export type EditorItem = { id: string; [key: string]: string };

interface Props {
  title: string;
  description?: string;
  items: EditorItem[];
  onChange: (items: EditorItem[]) => void;
  fields: ItemField[];
  itemName: string;
  addLabel: string;
  newItem: () => EditorItem;
}

const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-600 mb-1";
const smallBtnCls = "text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer disabled:opacity-30";

export default function ItemsEditor({ title, description, items, onChange, fields, itemName, addLabel, newItem }: Props) {
  function update(id: string, key: string, value: string) {
    onChange(items.map((it) => (it.id === id ? { ...it, [key]: value } : it)));
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h2 className="font-heading font-semibold text-foreground-900">{title}</h2>
      {description && <p className="mt-1 text-xs text-foreground-500">{description}</p>}

      <div className="mt-4 space-y-3">
        {items.map((item, i) => (
          <div key={item.id} className="rounded-md border border-background-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-foreground-500">
                {itemName} {i + 1}
              </span>
              <div className="flex gap-3">
                <button type="button" className={smallBtnCls} disabled={i === 0} onClick={() => move(i, -1)}>
                  ↑ Subir
                </button>
                <button
                  type="button"
                  className={smallBtnCls}
                  disabled={i === items.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓ Bajar
                </button>
                <button
                  type="button"
                  className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer"
                  onClick={() => onChange(items.filter((it) => it.id !== item.id))}
                >
                  Eliminar
                </button>
              </div>
            </div>

            <div className="space-y-2.5">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}</label>
                  <div className="flex items-center gap-2">
                    {f.key === "icono" && (
                      <span
                        className="w-9 h-9 shrink-0 rounded-md bg-primary-900 text-accent-300 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <i className={`${item[f.key] ?? ""} text-lg`} />
                      </span>
                    )}
                    {f.type === "textarea" ? (
                      <textarea
                        className={inputCls}
                        rows={2}
                        value={item[f.key] ?? ""}
                        onChange={(e) => update(item.id, f.key, e.target.value)}
                      />
                    ) : (
                      <input
                        className={inputCls}
                        value={item[f.key] ?? ""}
                        onChange={(e) => update(item.id, f.key, e.target.value)}
                      />
                    )}
                  </div>
                  {f.hint && <p className="mt-1 text-xs text-foreground-500">{f.hint}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}

        {items.length === 0 && <p className="text-sm text-foreground-500">Todavía no hay ninguno. Agrega el primero.</p>}

        <button
          type="button"
          onClick={() => onChange([...items, newItem()])}
          className="w-full rounded-lg border-2 border-dashed border-background-300 py-2.5 text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-600 cursor-pointer"
        >
          {addLabel}
        </button>
      </div>
    </section>
  );
}
